import {
  employmentActivityOptions,
  employmentActivityPvqOptions,
  employmentMilitaryBranchValues,
  militaryEmploymentOptions,
  otherEmploymentOptions,
  SELF_EMPLOYMENT,
  UNEMPLOYMENT,
} from 'constants/enums/employmentOptions';
import { DEFAULT_LATEST, TODAY } from 'constants/dateLimits';
import {
  today, dateNoDayWithinRange, setToStartOfMonth, getSafeDateTime, getApplicantRequiredDuration, getThresholdYears, isSafeDateLater
} from 'helpers/date';
import { isInternational, isPO } from 'helpers/location';
import { checkValue, hasYesOrNo } from 'models/validate';
import physicalAddress from 'forms/shared/validators/models/physicalAddress/physicalAddress';
import name from 'forms/shared/validators/models/name/name';
import locationAddress from 'forms/shared/validators/models/location/models/address';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import { ValidateJSModel } from 'forms/shared/types/validatejs';
import { DateRangeModel } from 'forms/shared/validators/models/dateRange/types';
import { isLocationModelDefined } from 'forms/shared/validators/models/location/utility';
import { SF852013, SF85 } from 'constants/formTypes';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';
import { DeepSafePartial } from 'types/types';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { HistoryEmploymentItemModel, EmploymentActivityModel } from '../types';
import {
  historyEmploymentReprimandValidator,
  pvqHistoryEmploymentReprimandValidator,
  reasonLeftValidator,
  supervisorValidator,
  pvqSupervisorValidator,
  historyEmploymentAdditionalValidator,
  pvqAssignmentValidator,
  pvqDismissalItemValidator,
  pvqReasonLeftValidator
} from './fields';
import { FormDetails } from '../../../../../util/singletons';

const withinYears = (dateRange: DeepSafePartial<DateRangeModel> = {}, years = 7): boolean => {
  const { from, present } = dateRange;
  const to = present ? today.toObject() : dateRange.to;
  const employmentTimeFrame = { years };

  if (to === undefined || from === undefined) { return false; }

  return (
    dateNoDayWithinRange(to, employmentTimeFrame) || dateNoDayWithinRange(from, employmentTimeFrame)
  );
};

const matchEmploymentActivity = (
  attributes: DeepSafePartial<HistoryEmploymentItemModel>,
  activities: string[]
): boolean => attributes?.EmploymentActivity?.value !== undefined
  && activities.includes(attributes?.EmploymentActivity.value);

const isSF852013 = () => {
  const formDetails = FormDetails.getInstance().getData();
  return (
    (formDetails.formType === SF85 || formDetails.formType === SF852013)
    && formDetails.formVersion === '2013-12'
  );
};

export const historyEmploymentItemValidator: ValidateJSModel<DeepSafePartial<HistoryEmploymentItemModel>> = {
  EmploymentActivity: (value, attributes, attributeName, options) => {
    const formEmploymentActivityOptions = employmentActivityOptions;
    return {
      presence: true,
      model: {
        validator: {
          value: {
            presence: true,
            inclusion: formEmploymentActivityOptions,
          },
          otherExplanation: (value, attributes: DeepSafePartial<EmploymentActivityModel>) => {
            if (attributes?.value && attributes?.value === 'Other') {
              return { presence: true };
            }
            return {};
          },
        },
      },
    };
  },

  Dates: (value, attributes, attributeName, options = {}) => {
    const { applicantBirthdate } = options;
    const thresholdYears = getThresholdYears(options?.formType);
    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && setToStartOfMonth(getSafeDateTime(getSafeDateTime(options?.applicantBirthdate)?.datetime?.plus({ years: 18 })));

    const { coverage } = getApplicantRequiredDuration({
      requiredDuration: options?.requireYears ? { years: options?.requireYears } : undefined,
      applicantBirthdate,
      thresholdYears,
      type: 'employment'
    });

    const toDate = getSafeDateTime(value?.to);
    const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus(coverage)));
    let earliestDate;
    let earliestMessage;

    if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid && requiredDate?.valid && isSafeDateLater(requiredDate, applicantEighteenthBirthdate)) {
      earliestDate = applicantEighteenthBirthdate;
      earliestMessage = 'error.beforeDuration';
    } else if (requiredDate?.valid) {
      earliestDate = requiredDate;
      earliestMessage = 'error.beforeDuration';
    }

    const beyondRequired = (toDate?.valid && earliestDate?.valid)
      ? isSafeDateLater(earliestDate, toDate) : false;

    return {
      presence: true,
      daterange: {
        requireYear: false,
        earliest: beyondRequired ? earliestDate.datetime : applicantBirthdate,
        latest: DEFAULT_LATEST,
        tooEarly: beyondRequired ? earliestMessage : 'error.applicantMin',
      },
    };
  },

  // Required by all but Unemployment
  Title: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [UNEMPLOYMENT])) { return {}; }
    return { presence: true, hasValue: true };
  },

  Status: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [UNEMPLOYMENT]) || !options?.requireEmploymentItemStatus) { return {}; }
    return { presence: true, hasValue: true };
  },

  Address: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [UNEMPLOYMENT])) { return {}; }
    return {
      presence: true,
      location: {
        validator: locationAddress,
      },
    };
  },

  AlternateAddress: (value, attributes, attributeName, options) => {
    // This does not apply to an SF85 2013
    if (isSF852013()) { return {}; }

    if (matchEmploymentActivity(attributes, [UNEMPLOYMENT])) { return {}; }
    if (attributes?.PhysicalAddress?.HasDifferentAddress?.value !== 'Yes') {
      if (attributes?.Address && isInternational(attributes?.Address)) {
        return {
          presence: true,
          model: {
            validator: physicalAddress,
            militaryAddress: true,
            onlyMilitaryAddress: true,
            hasTelephone: false,
          },
        };
      }

      if (attributes?.Address && isPO(attributes?.Address)) {
        return {
          presence: true,
          model: {
            validator: physicalAddress,
            militaryAddress: false,
            hasTelephone: false,
          },
        };
      }
    }

    return {};
  },

  Telephone: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [UNEMPLOYMENT])) { return {}; }
    return {
      presence: true,
      model: { validator: phoneModelValidator, requireNumber: true },
    };
  },

  ReasonLeft: (value, attributes, attributeName, options) => {
    const { Dates, Additional } = attributes;
    if (Dates?.present === true || matchEmploymentActivity(attributes, [UNEMPLOYMENT])) { return {}; }
    if (options?.requireEmploymentItemLeft) {
      const extendedOptions = { ...options, Dates };
      const additionalDates = [];

      if (Additional?.items) {
        for (const item of Additional.items) {
          additionalDates.push(item?.Item?.DatesEmployed);
        }
      }

      return {
        presence: true,
        model: {
          validator: reasonLeftValidator(extendedOptions),
          Dates,
          additionalDates,
        },
      };
    }
    return {};
  },

  Assignments: (value, attributes, attributeName, options) => ({}),

  Reprimand: (value, attributes, attributeName, options) => {
    if (options?.requireEmploymentItemLeft && !matchEmploymentActivity(attributes, [UNEMPLOYMENT])) {
      const { Dates, Additional } = attributes;
      const additionalDates = [];

      if (Additional?.items) {
        for (const item of Additional.items) {
          additionalDates.push(item?.Item?.DatesEmployed);
        }
      }

      if (withinYears(Dates, options?.recordYears)) {
        return {
          presence: true,
          branchCollection: {
            validator: historyEmploymentReprimandValidator,
            Dates,
            additionalDates,
          },
        };
      }
    }

    return {};
  },

  // Required by military & other
  Supervisor: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [...militaryEmploymentOptions, ...otherEmploymentOptions])) {
      return {
        presence: true,
        model: {
          validator: supervisorValidator,
          requireEmploymentHistorySupervisorEmail: options?.requireEmploymentHistorySupervisorEmail,
        },
      };
    }

    return {};
  },

  // Required by other & self-employment
  Employer: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [...otherEmploymentOptions, SELF_EMPLOYMENT])) {
      return { presence: true, hasValue: true };
    }

    return {};
  },

  PhysicalAddress: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [...otherEmploymentOptions])
      || (matchEmploymentActivity(attributes, [SELF_EMPLOYMENT])
        && options?.requireEmploymentSelfEmployedJobLocation)) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          requireTelephone: !isPVQForm(options?.formType),
        },
      };
    }

    return {};
  },

  PhysicalAlternateAddress: (value, attributes, attributeName, options) => {
    const { PhysicalAddress } = attributes;
    const shouldValidate = matchEmploymentActivity(attributes, [...otherEmploymentOptions])
      || (matchEmploymentActivity(attributes, [SELF_EMPLOYMENT]) && options?.requireEmploymentSelfEmployedJobLocation);
    // This does not apply to an SF85 2013
    if (isSF852013()) { return {}; }

    // All other forms it applies to
    if (shouldValidate && PhysicalAddress && checkValue(PhysicalAddress.HasDifferentAddress, 'Yes')) {
      const { Address } = PhysicalAddress;
      if (Address && isInternational(Address)) {
        return {
          presence: true,
          model: {
            validator: physicalAddress,
            militaryAddress: true,
            onlyMilitaryAddress: true,
            hasTelephone: false,
          },
        };
      }

      if (Address && isPO(Address)) {
        return {
          presence: true,
          model: {
            validator: physicalAddress,
            militaryAddress: false,
            hasTelephone: false,
          },
        };
      }
    }

    return {};
  },

  // Required by self-employment & unemployed
  ReferenceName: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [SELF_EMPLOYMENT, UNEMPLOYMENT])) {
      return {
        presence: true,
        model: {
          validator: name,
          hideMiddleName: !isPVQForm(options?.formType),
        },
      };
    }

    return {};
  },

  ReferencePhone: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [SELF_EMPLOYMENT, UNEMPLOYMENT])) {
      return {
        presence: true,
        model: { validator: phoneModelValidator, requireNumber: true },
      };
    }

    return {};
  },

  ReferenceAddress: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, [SELF_EMPLOYMENT, UNEMPLOYMENT])) {
      return {
        presence: true,
        location: {
          validator: locationAddress,
        },
      };
    }

    return {};
  },

  ReferenceAlternateAddress: (value, attributes, attributeName, options) => {
    // This does not apply to an SF85 2013
    if (isSF852013()) { return {}; }
    if (
      matchEmploymentActivity(attributes, [SELF_EMPLOYMENT, UNEMPLOYMENT])
      && isLocationModelDefined(attributes?.ReferenceAddress)
    ) {
      if (isInternational(attributes?.ReferenceAddress)) {
        return {
          presence: true,
          model: {
            validator: physicalAddress,
            militaryAddress: true,
            onlyMilitaryAddress: true,
            hasTelephone: false,
          },
        };
      }

      if (isPO(attributes?.ReferenceAddress)) {
        return {
          presence: true,
          model: {
            validator: physicalAddress,
            militaryAddress: false,
            hasTelephone: false,
          },
        };
      }
    }

    return {};
  },

  SupervisorAlternateAddress: (value, attributes, attributeName, options) => {
    // This does not apply to an SF85 2013
    if (isSF852013()) { return {}; }

    if (matchEmploymentActivity(attributes, [...militaryEmploymentOptions, ...otherEmploymentOptions])) {
      const address = attributes?.Supervisor?.Address;
      if (isLocationModelDefined(address)) {
        if (isInternational(address)) {
          return {
            presence: true,
            model: {
              validator: physicalAddress,
              militaryAddress: true,
              onlyMilitaryAddress: true
            },
          };
        }

        if (isPO(address)) {
          return {
            presence: true,
            model: { validator: physicalAddress, militaryAddress: false },
          };
        }
      }
    }

    return {};
  },

  MilitaryBranch: (value, attributes, attributeName, options) => ({}),

  // Required by military
  DutyStation: (value, attributes, attributeName, options) => {
    if (matchEmploymentActivity(attributes, militaryEmploymentOptions)) {
      return { presence: true, hasValue: true };
    }

    return {};
  },

  // Applies to other employment
  Additional: (value, attributes, attributeName, options) => {
    // TODO: Fix locale for the latest. Right now, it is returning "This cannot be in the future"
    if (matchEmploymentActivity(attributes, otherEmploymentOptions)) {
      return {
        branchCollection: {
          validator: historyEmploymentAdditionalValidator,
          latest: attributes?.Dates && attributes?.Dates.from,
        },
      };
    }

    if (value?.items && matchEmploymentActivity(attributes, [SELF_EMPLOYMENT, UNEMPLOYMENT]) && value?.items.length > 0) {
      for (let i = 0; i < value.items?.length; i++) {
        if (value?.items[i]?.Item?.Has?.value === 'Yes') {
          return {
            requireTrue: { // force error message; value will never be true
              message: 'error.additionalActivity.invalidType',
            }
          };
        }
      }
    }

    return {};
  },
};

export const pvqHistoryEmploymentItemValidator: ValidateJSModel<DeepSafePartial<HistoryEmploymentItemModel>> = {
  EmploymentActivity: (value, attributes, attributeName, options) => ({
    presence: true,
    model: {
      validator: {
        value: {
          presence: true,
          inclusion: employmentActivityPvqOptions,
        }
      },
    },
  }),

  Dates: (value, attributes, attributeName, options = {}) => {
    const { applicantBirthdate } = options;
    const thresholdYears = getThresholdYears(options.formType);
    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && setToStartOfMonth(getSafeDateTime(getSafeDateTime(options.applicantBirthdate)?.datetime?.plus({ years: 18 })));

    const { coverage } = getApplicantRequiredDuration({
      requiredDuration: options.requireYears ? { years: options.requireYears } : undefined,
      applicantBirthdate,
      thresholdYears,
      type: 'employment'
    });

    const toDate = getSafeDateTime(value?.to);
    const requiredDate = setToStartOfMonth(getSafeDateTime(TODAY.minus(coverage)));
    let earliestDate;
    let earliestMessage;

    if (options.formType && options.formType === 'SF86' && applicantEighteenthBirthdate?.valid && requiredDate?.valid && isSafeDateLater(requiredDate, applicantEighteenthBirthdate)) {
      earliestDate = applicantEighteenthBirthdate;
      earliestMessage = 'error.beforeDuration';
    } else if (requiredDate?.valid) {
      earliestDate = requiredDate;
      earliestMessage = 'error.beforeDuration';
    }

    const beyondRequired = (toDate?.valid && earliestDate?.valid)
      ? isSafeDateLater(earliestDate, toDate) : false;

    return {
      presence: true,
      daterange: {
        requireYear: false,
        earliest: beyondRequired ? earliestDate.datetime : applicantBirthdate,
        latest: DEFAULT_LATEST,
        tooEarly: beyondRequired ? earliestMessage : 'error.applicantMin',
      },
    };
  },

  MilitaryBranch: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        return {
          presence: true,
          hasValue: true,
          model: {
            validator: {
              value: {
                presence: true,
                inclusion: employmentMilitaryBranchValues.getValues(),
              }
            },
          },
        };
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
        break;
      case 'StateGovernment':
        break;
      case 'FederalContractor':
        break;
      case 'Other':
        break;
      case 'NonGovernment':
        break;
      default:
        return {};
    }
  },

  DutyStation: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        return { presence: true, hasValue: true };
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
        break;
      case 'StateGovernment':
        break;
      case 'FederalContractor':
        break;
      case 'Other':
        break;
      case 'NonGovernment':
        break;
      default:
        return {};
    }
    return {};
  },

  Address: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        return {
          presence: true,
          location: {
            validator: pvqLocationModelValidator,
            requireStreet: isSafeUnitedStates(value),
          }
        };
      case 'Unemployment':
        return {};
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.StillInBusiness?.value === 'Yes') {
          return {
            presence: true,
            location: {
              validator: pvqLocationModelValidator,
              requireStreet: true,
              requireIsMilitaryDiplomaticAddress: true,
              requireMilitaryDiplomaticInstallationName: true,
              requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
            }
          };
        }
        return {};
      case 'SelfEmployment':
        if (attributes.HasAssociatedCompany?.value === 'Yes' && attributes?.IsHomeCompany?.value === 'No') {
          return {
            presence: true,
            location: {
              validator: pvqLocationModelValidator,
              requireStreet: true,
              requireIsMilitaryDiplomaticAddress: true,
              requireMilitaryDiplomaticInstallationName: true,
              requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
            }
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  Assignments: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        return {
          branchCollection: {
            validator: pvqAssignmentValidator,
            length: {
              minimum: 1
            }
          },
        };
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
        break;
      case 'StateGovernment':
        break;
      case 'FederalContractor':
        break;
      case 'Other':
        break;
      case 'NonGovernment':
        break;
      default:
        return {};
    }
  },

  Supervisor: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          model: {
            validator: pvqSupervisorValidator,
            hasMultiEmail: true,
            hasMultiTelephone: true,
          },
        };
      case 'USMilitary':
        return {
          presence: true,
          model: {
            validator: pvqSupervisorValidator,
            isMilitarySupervisor: true,
          },
        };
      case 'Unemployment':
      case 'SelfEmployment':
        return {
          presence: true,
          model: {
            validator: pvqSupervisorValidator,
            requireStreet: true,
            hasNoTitle: true,
            hasMultiEmail: true,
            hasMultiTelephone: true,
          },
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  Reprimand: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    const { Dates, Additional } = attributes;
    const additionalDates = [];

    if (Additional?.items) {
      for (const item of Additional.items) {
        additionalDates.push(item?.Item?.DatesEmployed);
      }
    }

    switch (activity) {
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          branchCollection: {
            validator: pvqHistoryEmploymentReprimandValidator,
            Dates,
            additionalDates,
            requireTelephone: true,
            requireEmail: true,
          },
        };
      case 'USMilitary':
        return {
          presence: true,
          branchCollection: {
            validator: pvqHistoryEmploymentReprimandValidator,
            Dates,
            additionalDates,
            isMilitaryReprimand: true
          },
        };
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'Other':
        break;
      default:
        return {};
    }
  },

  Employer: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        if (attributes.HasAssociatedCompany?.value === 'Yes') {
          return { presence: true, hasValue: true };
        }
        return {};
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return { presence: true, hasValue: true };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  StillInBusiness: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        if (attributes.HasAssociatedCompany?.value === 'Yes') {
          return { presence: true, hasValue: true };
        }
        return {};
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return { presence: true, hasValue: { validator: hasYesOrNo } };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  Telephone: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.StillInBusiness?.value === 'Yes') {
          return {
            presence: true,
            model: {
              validator: phoneModelValidator,
              requireNumber: true,
              requireNumberType: true,
            }
          };
        }
        return {};
      case 'SelfEmployment':
        if (attributes.HasAssociatedCompany?.value === 'Yes') {
          return {
            presence: true,
            model: {
              validator: phoneModelValidator,
              requireNumber: true,
              requireNumberType: true,
            }
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  HasDifferentPhysicalWorkAddress: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        if (attributes.HasAssociatedCompany?.value === 'Yes') {
          return {
            presence: true,
            hasValue: {
              validator: hasYesOrNo
            }
          };
        }
        return {};
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  PhysicalWorkAddress: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.HasDifferentPhysicalWorkAddress?.value === 'Yes') {
          return {
            presence: true,
            location: {
              validator: pvqLocationModelValidator,
              requireStreet: true,
              requireIsMilitaryDiplomaticAddress: true,
              requireMilitaryDiplomaticInstallationName: true,
              requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
            }
          };
        }
        break;
      case 'SelfEmployment':
        if (attributes.HasAssociatedCompany?.value === 'Yes' && attributes?.HasDifferentPhysicalWorkAddress?.value === 'Yes') {
          return {
            presence: true,
            location: {
              validator: pvqLocationModelValidator,
              requireStreet: true,
              requireIsMilitaryDiplomaticAddress: true,
              requireMilitaryDiplomaticInstallationName: true,
              requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
            }
          };
        }
      case 'Other':
        break;
      default:
        return {};
    }
  },

  Title: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return { presence: true, hasValue: true };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  IsInternship: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  Status: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: true
        };

      case 'Other':
        break;
      default:
        return {};
    }
  },

  WasFired: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  FiredDetails: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    const { Dates, Additional } = attributes;
    const additionalDates = [];

    if (Additional?.items) {
      for (const item of Additional.items) {
        additionalDates.push(item?.Item?.DatesEmployed);
      }
    }

    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.WasFired?.value === 'Yes') {
          return {
            presence: true,
            model: {
              validator: pvqDismissalItemValidator,
              hasDate: true,
              Dates,
              additionalDates,
            },
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  WasLeaveBeforeFired: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  LeaveBeforeFiredDetails: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.WasLeaveBeforeFired?.value === 'Yes') {
          return {
            presence: true,
            model: {
              validator: pvqDismissalItemValidator,
            },
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  WasLeaveAfterNoticeMisconduct: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  LeaveAfterNoticeMisconductDetails: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.WasLeaveAfterNoticeMisconduct?.value === 'Yes') {
          return {
            presence: true,
            model: {
              validator: pvqDismissalItemValidator,
            },
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  WasLeaveAfterNoticePerformance: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  LeaveAfterNoticePerformanceDetails: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.WasLeaveAfterNoticePerformance?.value === 'Yes') {
          return {
            presence: true,
            model: {
              validator: pvqDismissalItemValidator,
            },
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  WasLeavePendingOutcome: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  LeavePendingOutcomeDetails: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.WasLeavePendingOutcome?.value === 'Yes') {
          return {
            presence: true,
            model: {
              validator: pvqDismissalItemValidator,
            },
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  ReasonLeft: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'NonGovernment':
      case 'FederalContractor':
        if (attributes?.Dates?.present !== true
        ) {
          return {
            presence: true,
            model: {
              validator: pvqReasonLeftValidator
            },
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  ReturnedToWork: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'Other':
        break;
      default:
        return {};
    }
  },

  ReturnDate: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        break;
      case 'OtherFederal':
      case 'StateGovernment':
      case 'FederalContractor':
      case 'NonGovernment':
        if (attributes?.ReturnedToWork?.value === 'Yes') {
          const previousQuitDate = attributes?.Dates?.to;
          if (!previousQuitDate) { return {}; }
          const safePreviousQuitDate = setToStartOfMonth(getSafeDateTime(previousQuitDate));
          return {
            presence: true,
            daterange: {
              requireYear: false,
              earliest: safePreviousQuitDate.datetime,
              latest: DEFAULT_LATEST,
              tooEarly: 'error.date.returnToWorkTooEarly',
            },
          };
        }
        return {};
      case 'Other':
        break;
      default:
        return {};
    }
  },

  HasAssociatedCompany: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        return {
          presence: true,
          hasValue: {
            validator: hasYesOrNo
          }
        };
      case 'OtherFederal':
        break;
      case 'StateGovernment':
        break;
      case 'FederalContractor':
        break;
      case 'Other':
        break;
      case 'NonGovernment':
        break;
      default:
        return {};
    }
  },

  SelfEmployedEmail: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        if (attributes?.SelfEmployedEmailNotApplicable?.applicable && attributes.HasAssociatedCompany?.value === 'Yes') {
          return {
            presence: true,
            hasValue: true
          };
        }
        return {};
      case 'OtherFederal':
        break;
      case 'StateGovernment':
        break;
      case 'FederalContractor':
        break;
      case 'Other':
        break;
      case 'NonGovernment':
        break;
      default:
        return {};
    }
  },

  SelfEmployedWebsite: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        if (attributes?.SelfEmployedWebsiteNotApplicable?.applicable && attributes.HasAssociatedCompany?.value === 'Yes') {
          return {
            presence: true,
            hasValue: true
          };
        }
        return {};
      case 'OtherFederal':
        break;
      case 'StateGovernment':
        break;
      case 'FederalContractor':
        break;
      case 'Other':
        break;
      case 'NonGovernment':
        break;
      default:
        return {};
    }
  },

  IsHomeCompany: (value, attributes, attributeName, options) => {
    const activity = attributes?.EmploymentActivity?.value;
    switch (activity) {
      case 'USMilitary':
        break;
      case 'Unemployment':
        break;
      case 'SelfEmployment':
        if (attributes.HasAssociatedCompany?.value === 'Yes') {
          return {
            presence: true,
            hasValue: {
              validator: hasYesOrNo
            }
          };
        }
        return {};
      case 'OtherFederal':
        break;
      case 'StateGovernment':
        break;
      case 'FederalContractor':
        break;
      case 'Other':
        break;
      case 'NonGovernment':
        break;
      default:
        return {};
    }
  },
};
